import {
    normalizeBreakpointsForEditor,
    normalizeWidgetsForEditor,
} from './DashboardWidgetDataProviderNormalization';
import { buildDashboardPayloadForSave } from './DashboardWidgetDataProviderPayload';
import { applyDashboardEditorStateFromResponse } from './DashboardWidgetDataProviderState';
import {
    fetchDashboardEditorData,
    fetchDashboardEditorInterface,
    saveDashboardEditorData,
} from './DashboardWidgetDataProviderRequests';

/**
 * Dashboard-model specific mapping and persistence methods for the provider.
 */
const dashboardWidgetDataProviderDashboard = {
    /**
     * Get dashboard from response.
     *
     * @param {*} data
     */
    getDashboardFromResponse(data) {
        if (data && data.dashboard) {
            return data.dashboard;
        }

        return data || {};
    },

    /**
     * Return a consistent fallback tab so widget normalization and save payloads
     * can always point to a valid tab id.
     */
    getDefaultDashboardTab() {
        const tabs = this.normalizeDashboardTabs(this.dashboardTabs);
        return tabs[0];
    },

    /**
     * Normalize backend dashboard tabs into editor state.
     *
     * @param {*} tabs
     */
    normalizeDashboardTabs(tabs) {
        const sourceTabs = Array.isArray(tabs) ? tabs : [];
        const normalizedTabs = [];

        for (let tabIndex = 0; tabIndex < sourceTabs.length; tabIndex += 1) {
            const tab = sourceTabs[tabIndex];
            const normalizedTab = {
                id: tab?.id || `tab-${tabIndex + 1}`,
                title: tab?.title || `${this.translate('Tab')} ${tabIndex + 1}`,
            };

            if (!normalizedTab.id) {
                continue;
            }

            normalizedTabs.push(normalizedTab);
        }

        if (normalizedTabs.length) {
            return normalizedTabs;
        }

        return [
            {
                id: 'default',
                title: `${this.translate('Tab')} 1`,
            },
        ];
    },

    /**
     * Build the default media query shape used when the backend has not defined
     * breakpoint metadata yet.
     */
    createDefaultBreakpointMediaQuery() {
        return {
            mediaQuerySubList: [
                {
                    mediaTypes: ['screen'],
                    features: [
                        {
                            name: 'max-width',
                            value: '100%',
                        },
                    ],
                },
            ],
        };
    },

    /**
     * Resolve the breakpoint definitions that should drive the editor.
     *
     * The interface response is the preferred source. Saved breakpoints are
     * reused as a fallback when a dashboard predates the current interface
     * config, and a desktop fallback keeps brand-new dashboards editable.
     */
    getSupportedBreakpointDefinitions(savedBreakpoints = []) {
        const supportedBreakpoints = Array.isArray(this.supportedBreakpoints)
            ? this.supportedBreakpoints
            : [];

        if (supportedBreakpoints.length) {
            return supportedBreakpoints;
        }

        if (savedBreakpoints.length) {
            return savedBreakpoints;
        }

        return [
            {
                id: 'desktop',
                title: this.translate('Desktop'),
                isOriginal: true,
            },
        ];
    },

    /**
     * Guarantee that one breakpoint is marked as the persisted source.
     *
     * @param {*} breakpoints
     */
    hasOriginalBreakpoint(breakpoints) {
        for (const breakpoint of breakpoints) {
            if (breakpoint.isOriginal) {
                return true;
            }
        }

        return false;
    },

    /**
     * Normalize backend breakpoint data into editor state.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    normalizeBreakpoints(breakpoints, widgets) {
        return normalizeBreakpointsForEditor(this, breakpoints, widgets);
    },

    /**
     * Normalize backend widget data into editor state.
     *
     * @param {*} widgets
     */
    normalizeWidgets(widgets) {
        return normalizeWidgetsForEditor(this, widgets);
    },

    /**
     * Parse dashboard JSON data with a safe fallback.
     *
     * @param {*} value
     * @param {*} fallback
     */
    parseJsonData(value, fallback = {}) {
        if (value === null || typeof value === 'undefined' || value === '') {
            return fallback;
        }

        if (typeof value === 'object') {
            return value;
        }

        try {
            return JSON.parse(value);
        } catch (error) {
            return fallback;
        }
    },

    /**
     * Fetch dashboard blueprint data from the backend.
     */
    async fetchBlueprintData() {
        if (!this.features || !this.grid || !this.supportedWidgets || !this.supportedBreakpoints) {
            await this.fetchInterfaceData();
        }

        applyDashboardEditorStateFromResponse(this, await fetchDashboardEditorData(this));
    },

    /**
     * Fetch dashboard interface data from the backend.
     */
    async fetchInterfaceData() {
        const data = await fetchDashboardEditorInterface(this);

        if (!data) {
            return;
        }

        this.widgetGroupings = data.widgetGroupings;
        this.supportedWidgets = data.supportedWidgets;
        this.supportedBreakpoints = data.supportedBreakpoints;
        this.listUrl = data.listUrl;
        this.listLabel = data.listLabel;
        this.topBarWidgets = data.topBarWidgets;
        this.placeholderUrl = data.placeholderUrl;
        this.placeholderHandler = null;
        this.versionsActive = data.versionsActive;
        this.versionsCount = data.versionsCount;
        this.features = data.features;
        this.grid = data.grid;
        this.editorContext = data.editorContext || null;
    },

    /**
     * Get the dashboard editor context used by child components.
     */
    getEditorContext() {
        return this.editorContext || null;
    },

    /**
     * Build the dashboard payload sent to the backend.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    buildDashboardPayload(breakpoints, widgets) {
        return buildDashboardPayloadForSave(this, breakpoints, widgets);
    },

    /**
     * Save the current dashboard editor state.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    async save(breakpoints, widgets) {
        const result = await saveDashboardEditorData(this, breakpoints, widgets);

        applyDashboardEditorStateFromResponse(this, result);

        return {
            todos: result?.todos || [],
        };
    },

    /**
     * Get settings form url.
     *
     * @param {*} widgetType
     * @param {*} verticalId
     * @param {*} blueprintId
     * @param {*} hashId
     */
    getSettingsFormUrl(widgetType, verticalId, blueprintId, hashId) {
        const url = new URL(this.getDashboardLayoutEditorUrl(`/settings/widget/${widgetType}${hashId ? '/' + hashId : '/new'}`));
        this.appendSettingsRequestParams(url, verticalId);

        return url.toString();
    },

    /**
     * Get save settings form url.
     *
     * @param {*} widgetType
     * @param {*} verticalId
     * @param {*} blueprintId
     * @param {*} hashId
     */
    getSaveSettingsFormUrl(widgetType, verticalId, blueprintId, hashId) {
        const url = new URL(this.getDashboardLayoutEditorUrl(`/settings/widget/${widgetType}${hashId ? '/' + hashId : '/new'}/post`));
        this.appendSettingsRequestParams(url, verticalId);

        return url.toString();
    },

    /**
     * The widget settings endpoints need the same query params for both fetch
     * and save, so keep the url decoration in one helper.
     *
     * @param {*} url
     * @param {*} verticalId
     */
    appendSettingsRequestParams(url, verticalId) {
        if (verticalId) {
            url.searchParams.append('verticalId', verticalId);
        }

        this.appendQueryParams(url, this.getEditorRequestParams());
    },
};

export default dashboardWidgetDataProviderDashboard;
